import { defineNuxtPlugin } from '#app/nuxt';

export type IConstants = typeof constants;
export type ConstantsKeys = keyof IConstants;
export default defineNuxtPlugin(() => {
  return {
    provide: {
      const: <K extends ConstantsKeys>(key: K): IConstants[K] => constants[key],
    },
  };
});

export const APPSIGNAL_NAMESPACES = {
  DEFAULT: 'Vue',
  AXIOS: 'Axios',
  I18N: 'I18N',
};

export const APPTEGY_ACCOUNTS = [
  'media',
  'rooms',
  'engage',
  'userManagement',
  'appManager',
  'inbox',
  'infolink',
  'navigate',
  'intranet',
  'communityEngagement',
  'dataManagement',
  'analytics',
  'tasks',
  'attendancePro',
  'smartMove',
];

const constants = Object.freeze({
  apptegyClientId: 1,
  platformRoles: [
    { label: 'Moderated User', value: 'moderated_user' },
    { label: 'User', value: 'user' },
    { label: 'Admin', value: 'admin' },
    { label: 'Super Admin', value: 'super_admin' },
  ],
  columns: [
    {
      prop: 'name',
      label: 'Name',
      width: 250,
      fixed: 'left',
    },
    {
      prop: 'email',
      label: 'Email',
      width: 250,
      fixed: 'left',
    },
    {
      prop: 'status',
      label: 'Status',
      width: 200,
    },
    {
      prop: 'administration',
      label: 'Administration',
      width: 200,
    },
    {
      prop: 'rooms',
      label: 'Connect',
      width: 200,
    },
    {
      prop: 'media',
      label: 'Media',
      width: 200,
    },
    {
      prop: 'engage',
      label: 'Engage',
      width: 200,
    },
    {
      prop: 'navigate',
      label: 'Navigate',
      width: 200,
    },
    {
      prop: 'intranet',
      label: 'Intranet',
      width: 200,
    },
    {
      prop: 'communityEngagement',
      label: 'Community Engagement',
      width: 200,
    },
    // Using two columns as custom actions to fake out the bugs from elemen.io Table Component.
    {
      label: '',
      prop: 'edit',
      fixed: 'right',
    },
    {
      label: '',
      prop: 'edit_two',
      fixed: 'right',
    },
  ],
  sortableColumns: ['given-name', 'family-name', 'email'],
  schoolTypes: [
    { name: 'Catholic School', value: 'catholicSchool' },
    { name: 'District Organization', value: 'schoolDistrict' },
    { name: 'Private Organization', value: 'privateOrganization' },
    { name: 'Public School', value: 'publicOrganization' },
    { name: 'Charter School', value: 'charterOrganization' },
    { name: 'Military Base School', value: 'militaryBaseSchool' },
    { name: 'International Client', value: 'internationalClient' },
    { name: 'Municipality', value: 'municipality' },
  ],
  sisProviders: [
    { name: 'None', value: null },
    { name: 'Skyward', value: 'skyward' },
    { name: 'PowerSchool', value: 'powerschool' },
    { name: 'Infinite Campus', value: 'infinite_campus' },
    { name: 'Other', value: 'other' },
  ],
  adminColumns: [
    { prop: 'id', label: 'Client ID', minWidth: 100 },
    { prop: 'name', label: 'Client Name', minWidth: 200 },
    { prop: 'subdomain', label: 'Subdomain', minWidth: 320 },
    { prop: 'city', label: 'City', minWidth: 110 },
    { prop: 'state', label: 'State', minWidth: 130 },
    { prop: 'country', label: 'Country', minWidth: 120 },
    { prop: 'app-access', label: 'App Access', minWidth: 170 },
  ],
  adminSortableColumns: ['id', 'name', 'city', 'state', 'country'],
  mediaOauthId: 2,
  intranetOauthId: 26,
  roomsOauthId: 5,
  userManagementOauthId: 6,
  authServiceOauthId: 1,
  appManagerOauthId: 10,
  engageOauthId: 12,
  inboxOauthId: 18,
  infolinkOauthId: 23,
  navigateOauthId: 17,
  analyticsOauthId: 51,
  tasksOauthId: 49,
  attendanceProOauthId: 52,
  smartMoveOauthId: 41,
  oauthIdsToNames: {
    1: 'auth_service',
    2: 'media',
    3: 'cms',
    4: 'teacher_pages',
    5: 'rooms',
    6: 'user_management',
    7: 'cms_v2',
    27: 'community_engagement',
    8: 'mobile_thrillshare',
    9: 'mobile_school_app',
    0: 'app_manager',
    11: 'theme_manager',
    12: 'engage',
    13: 'alerts_as_service',
    14: 'client_experience',
    15: 'files',
    16: 'url_shortener',
    17: 'navigate',
    18: 'inbox',
    19: 'roboto',
    20: 'lists_service',
    21: 'rooms_users_admin_service',
    23: 'infolink',
    24: 'payments',
    22: 'organizations_service',
    26: 'intranet',
    42: 'data_management',
    51: 'analytics',
    49: 'tasks',
    50: 'attendance_pro',
    41: 'smart_move',
  },
  urnClient: 'urn:apptegy:global:sso:client:',
  urnStaff: 'urn:apptegy:global:school-data:staff:',
  urnStudent: 'urn:apptegy:global:school-data:student:',
  userStatusByAction: {
    send_invite: ['No Invite', 'Pending Invite'],
    archive: ['No Invite', 'Pending Invite', 'Active', 'Locked'],
    revoke_invite: ['Pending Invite'],
    unarchive: ['Archive'],
    unlock: ['Locked'],
  },
  updateableEmailDomains: ['apptegy.com'],
});

export const GLOBAL_MESSAGING_MIN_GROUP_MEMBER_LIMIT = 3;
export const GLOBAL_MESSAGING_MAX_GROUP_MEMBER_LIMIT = 300;
